import { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import { useForm, FormProvider } from 'react-hook-form';
import { ModalBase } from '@components/Modal';
import { RadioButtonsGroup } from '@components/RadioButtonsGroup';
import { useTranslation } from '@hooks';
import { alertActions, menusActions } from '@actions';
import { menusService } from '@services';
import { handleApiError } from '@helpers/api';
import { VIEW_TYPE, PREVIEW_LOCALES } from '@constants/microsites';
import { DEFAULT_CURRENCY_BY_LOCALE } from '@constants/currencies';
import { MENU_TEXT_LIMITS, DEFAULT_SECTION_NAME_BY_LANGUAGE } from '@constants/menus';
import { LOCALE_KEY } from '@constants/locale';
import General from './General';
import Builder from './Builder';
import Preview from './Preview';
import { normalizeDocument, createSection, areOptionsEnabled, hasText } from '../helpers';

const Modal = styled(ModalBase)({
    '& .modal-base-paper': {
        outline: 'none',
        width: 'calc(100% - 32px)',
        height: 'calc(var(--100vh) - 32px)',
    },
    '& .modal-base-content': {
        display: 'grid',
        gridTemplateColumns: '50% 50%',
        '@media (max-width: 768px)': {
            gridTemplateColumns: '100%',
        },
    },
});

const FormWrapper = styled('div')({
    padding: '24px 16px',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    overflowX: 'hidden',
    '@media (max-width: 768px)': {
        overflowY: 'initial',
        overflowX: 'initial',
    },
});

const PreviewWrapper = styled('div')({
    backgroundColor: '#f2f2f2',
    overflowY: 'auto',
    padding: '24px 16px',
    '@media (max-width: 768px)': {
        overflowY: 'initial',
    },
});

const Tab = {
    General: 0,
    Builder: 1,
};

const EditModal = ({ modal, onClose, selectedMenu, account, page, setPage, rowsPerPage }) => {
    const { t } = useTranslation();
    const [tab, setTab] = useState(Tab.General);
    const dispatch = useDispatch();

    const defaultLanguage = PREVIEW_LOCALES[account?.locale] ?? LOCALE_KEY.GB;

    const isView = modal.type === VIEW_TYPE.VIEW;
    const isCreate = modal.type === VIEW_TYPE.CREATE;

    const defaultValues = useMemo(() => {
        if (selectedMenu) {
            return {
                id: selectedMenu.id,
                internalName: selectedMenu.internalName || '',
                currencyCode: selectedMenu.currencyCode || '',
                cuisines: selectedMenu.cuisines || [],
                publishExternally: selectedMenu.publishExternally ?? true,
                locationIds: selectedMenu.locationIds || [],
                document: selectedMenu.document || { names: [], descriptions: [], sections: [] },
            };
        }
        return {
            id: crypto.randomUUID(),
            internalName: '',
            currencyCode: DEFAULT_CURRENCY_BY_LOCALE[account?.locale] || 'USD',
            cuisines: [],
            publishExternally: false,
            locationIds: [],
            document: {
                names: [{ language: defaultLanguage, value: '' }],
                descriptions: [],
                sections: [
                    createSection(defaultLanguage, DEFAULT_SECTION_NAME_BY_LANGUAGE[defaultLanguage] || 'Main menu'),
                ],
            },
        };
    }, [selectedMenu, account?.locale, defaultLanguage]);

    const {
        setValue,
        control,
        watch,
        handleSubmit,
        getValues,
        formState: { isDirty, isSubmitting, errors },
    } = useForm({ mode: 'all', defaultValues });

    const internalName = watch('internalName');
    const currencyCode = watch('currencyCode');
    const document = watch('document');
    const locationIds = watch('locationIds');

    // Mirrors the backend MenuValidator save rules
    const validationIssues = useMemo(() => {
        const issues = [];

        const exceeds = (texts, limit) => (texts || []).some(text => (text.value || '').length > limit);

        if (!internalName?.trim()) issues.push(t('MenusPage.issueInternalName'));
        if (!hasText(document?.names)) issues.push(t('MenusPage.issueName'));
        if (exceeds(document?.names, MENU_TEXT_LIMITS.name)) issues.push(t('MenusPage.issueNameLength'));
        if (exceeds(document?.descriptions, MENU_TEXT_LIMITS.description)) {
            issues.push(t('MenusPage.issueDescriptionLength'));
        }
        if (!currencyCode) issues.push(t('MenusPage.issueCurrency'));
        if (!locationIds?.length) issues.push(t('MenusPage.issueLocations'));

        const sections = document?.sections || [];
        if (!sections.length) issues.push(t('MenusPage.issueSections'));
        if (sections.some(section => !hasText(section.names))) issues.push(t('MenusPage.issueSectionNames'));
        if (sections.length && sections.some(section => !(section.items || []).length)) {
            issues.push(t('MenusPage.issueSectionItems'));
        }

        const items = sections.flatMap(section => section.items || []);
        if (items.some(item => !hasText(item.names))) issues.push(t('MenusPage.issueItemNames'));
        if (items.some(item => item.priceAmount == null)) issues.push(t('MenusPage.issueItemPrices'));

        const options = items.flatMap(item => (areOptionsEnabled(item) ? item.options || [] : []));
        if (options.some(option => !hasText(option.names))) issues.push(t('MenusPage.issueOptionNames'));

        const allAttributes = items.map(item => item.attributes);
        if (allAttributes.some(attrs => exceeds(attrs?.ingredients, MENU_TEXT_LIMITS.ingredients))) {
            issues.push(t('MenusPage.issueIngredientsLength'));
        }

        return issues;
    }, [internalName, currencyCode, document, locationIds, t]);

    const isValid = validationIssues.length === 0;

    const onSave = async formData => {
        try {
            await menusService.saveMenu({
                ...formData,
                document: normalizeDocument(formData.document),
            });
        } catch (error) {
            handleApiError(error);
            return;
        }

        const targetPage = isCreate ? 0 : page;
        if (isCreate) setPage(0);
        dispatch(menusActions.getAll(targetPage, rowsPerPage));
        dispatch(alertActions.success(t(isCreate ? 'alertMessages.createSuccess' : 'alertMessages.editSuccess')));
        onClose();
    };

    return (
        <Modal
            fullScreen
            isOpen={modal.open}
            handleClose={onClose}
            isDirty={isDirty}
            title={t(isCreate ? 'MenusPage.createMenu' : isView ? 'MenusPage.modalTitle' : 'MenusPage.editMenu')}
            onPrimaryAction={isView ? null : handleSubmit(onSave)}
            primaryActionText={t('buttons.save')}
            primaryActionDisabled={isSubmitting || !isValid || (!isCreate && !isDirty)}
            primaryActionTooltip={
                validationIssues.length ? (
                    <div>
                        <div>{t('MenusPage.issuesTitle')}</div>
                        {validationIssues.map(issue => (
                            <div key={issue}>• {issue}</div>
                        ))}
                    </div>
                ) : (
                    ''
                )
            }
        >
            <FormProvider {...{ setValue, control, watch, getValues, errors }}>
                <FormWrapper>
                    <div style={{ marginBottom: 16 }}>
                        <RadioButtonsGroup
                            value={tab}
                            onChange={setTab}
                            options={[
                                { value: Tab.General, label: t('common.settings') },
                                { value: Tab.Builder, label: t('MenusPage.builder') },
                            ]}
                        />
                    </div>
                    {tab === Tab.General && <General isView={isView} defaultLanguage={defaultLanguage} />}
                    {tab === Tab.Builder && <Builder isView={isView} defaultLanguage={defaultLanguage} />}
                </FormWrapper>
                <PreviewWrapper>
                    <Preview defaultLanguage={defaultLanguage} isCreate={isCreate} />
                </PreviewWrapper>
            </FormProvider>
        </Modal>
    );
};

export default EditModal;
